import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ThemeContext = createContext(null);

// Color palettes matching backend
// Official Brand: True Navy #14213D, Metallic Gold #C5A059
const COLOR_PALETTES = {
  // Official Quantum Housing Conference Brand Theme
  'quantum-housing': { 
    primary: '#C5A059',      // Metallic Gold - accents & highlights
    secondary: '#14213D',    // True Navy - primary background & text
    accent: '#D4B06A',       // Lighter gold for hover states
    background: '#14213D',   // True Navy
    text: '#FFFFFF',         // White for contrast
    card_bg: '#1C2D4A'       // Slightly lighter navy for cards
  },
  default: { primary: '#C5A059', secondary: '#14213D', accent: '#D4B06A' },
  ocean: { primary: '#0077B6', secondary: '#00B4D8', accent: '#90E0EF' },
  sunset: { primary: '#F97316', secondary: '#FBBF24', accent: '#FB923C' },
  forest: { primary: '#059669', secondary: '#34D399', accent: '#10B981' },
  royal: { primary: '#7C3AED', secondary: '#A78BFA', accent: '#8B5CF6' },
  crimson: { primary: '#DC2626', secondary: '#F87171', accent: '#EF4444' },
  midnight: { primary: '#3B82F6', secondary: '#6366F1', accent: '#818CF8' },
  rose: { primary: '#F43F5E', secondary: '#FB7185', accent: '#FDA4AF' },
  emerald: { primary: '#10B981', secondary: '#6EE7B7', accent: '#34D399' },
  amber: { primary: '#F59E0B', secondary: '#FCD34D', accent: '#FBBF24' },
  slate: { primary: '#64748B', secondary: '#94A3B8', accent: '#CBD5E1' },
  teal: { primary: '#14B8A6', secondary: '#2DD4BF', accent: '#5EEAD4' },
  // Premium dark themes
  'hedge-miami': { 
    primary: '#A35C3C', secondary: '#B06A48', accent: '#D4956A',
    background: '#1E1E1E', text: '#F5F5DC', card_bg: '#282828'
  },
  'corporate-navy': { 
    primary: '#1E3A5F', secondary: '#D4AF37', accent: '#C5A028',
    background: '#0A1929', text: '#E8E8E8', card_bg: '#132F4C'
  },
  'luxury-black': { 
    primary: '#B76E79', secondary: '#E8B4BC', accent: '#DDA0A8',
    background: '#0D0D0D', text: '#FAFAFA', card_bg: '#1A1A1A'
  },
  'executive-charcoal': { 
    primary: '#00D9FF', secondary: '#0099CC', accent: '#66E5FF',
    background: '#121212', text: '#E0E0E0', card_bg: '#1E1E1E'
  },
};

// Convert hex to HSL for CSS variables
function hexToHSL(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function ThemeProvider({ children }) {
  const [siteSettings, setSiteSettings] = useState(null);
  const [currentThemeMode, setCurrentThemeMode] = useState('dim');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch site settings from CMS
  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cms/settings`);
      if (response.ok) {
        const data = await response.json();
        setSiteSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    
    // Poll for theme changes every 5 seconds to pick up CMS updates
    const pollInterval = setInterval(fetchSettings, 5000);
    
    // Also listen for storage events (when theme is changed in another tab/CMS)
    const handleStorageChange = (e) => {
      if (e.key === 'theme_updated') {
        fetchSettings();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchSettings]);

  // Apply theme settings when they change
  useEffect(() => {
    if (!siteSettings) return;

    const root = document.documentElement;
    const savedTheme = localStorage.getItem('theme') || 'dim';
    setCurrentThemeMode(savedTheme);

    // Get colors based on active palette or custom colors
    let colors = COLOR_PALETTES[siteSettings.active_palette] || COLOR_PALETTES.default;
    
    if (siteSettings.use_custom_colors) {
      const customColors = savedTheme === 'light' 
        ? siteSettings.custom_light 
        : savedTheme === 'dark' 
          ? siteSettings.custom_dark 
          : siteSettings.custom_dim;
      
      if (customColors) {
        colors = { ...colors, ...customColors };
      }
    }

    // Apply color CSS variables
    if (colors.primary) {
      const primaryHSL = hexToHSL(colors.primary);
      if (primaryHSL) root.style.setProperty('--primary', primaryHSL);
    }
    if (colors.secondary) {
      const secondaryHSL = hexToHSL(colors.secondary);
      if (secondaryHSL) root.style.setProperty('--secondary', secondaryHSL);
    }
    if (colors.accent) {
      const accentHSL = hexToHSL(colors.accent);
      if (accentHSL) root.style.setProperty('--accent', accentHSL);
    }

    // Apply dark theme-specific colors (background, text, card_bg) only for dark/dim modes
    // Light mode should use its own CSS defaults
    if (savedTheme !== 'light' && colors.background) {
      const bgHSL = hexToHSL(colors.background);
      if (bgHSL) {
        root.style.setProperty('--background', bgHSL);
        root.style.setProperty('--muted', bgHSL);
      }
    }
    if (savedTheme !== 'light' && colors.text) {
      const textHSL = hexToHSL(colors.text);
      if (textHSL) {
        root.style.setProperty('--foreground', textHSL);
        root.style.setProperty('--card-foreground', textHSL);
      }
    }
    if (savedTheme !== 'light' && colors.card_bg) {
      const cardHSL = hexToHSL(colors.card_bg);
      if (cardHSL) {
        root.style.setProperty('--card', cardHSL);
        root.style.setProperty('--popover', cardHSL);
      }
    }

    // Apply font settings
    if (siteSettings.heading_font) {
      // Load Google Font
      const existingLink = document.getElementById('cms-heading-font');
      if (existingLink) existingLink.remove();
      
      const link = document.createElement('link');
      link.id = 'cms-heading-font';
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${siteSettings.heading_font.replace(/ /g, '+')}:wght@300;400;500;600;700;900&display=swap`;
      document.head.appendChild(link);
      
      root.style.setProperty('--font-heading', `'${siteSettings.heading_font}', sans-serif`);
    }

    if (siteSettings.body_font) {
      const existingLink = document.getElementById('cms-body-font');
      if (existingLink) existingLink.remove();
      
      const link = document.createElement('link');
      link.id = 'cms-body-font';
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${siteSettings.body_font.replace(/ /g, '+')}:wght@300;400;500;600;700&display=swap`;
      document.head.appendChild(link);
      
      root.style.setProperty('--font-body', `'${siteSettings.body_font}', sans-serif`);
      document.body.style.fontFamily = `'${siteSettings.body_font}', -apple-system, BlinkMacSystemFont, sans-serif`;
    }

    // Apply font scale
    if (siteSettings.font_scale) {
      root.style.setProperty('--font-scale', siteSettings.font_scale);
    }

  }, [siteSettings, currentThemeMode]);

  // Listen for theme changes
  useEffect(() => {
    const handleThemeChange = () => {
      const theme = localStorage.getItem('theme') || 'dim';
      setCurrentThemeMode(theme);
    };

    window.addEventListener('storage', handleThemeChange);
    return () => window.removeEventListener('storage', handleThemeChange);
  }, []);

  const value = {
    siteSettings,
    currentThemeMode,
    setCurrentThemeMode,
    isLoading,
    refreshSettings: fetchSettings,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
